import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, retry, take} from 'rxjs/operators';
import * as Centrifuge from 'centrifuge';
import {NotificationsEnums, NotificationsInterfaces} from '@namespaces/notifications.namespace';
import {of} from 'rxjs';
import {NotificationsProvider} from '@providers/notifications/notifications.provider';
import {EventsService} from '@services/events.service';
import {ErrorLoggerService} from '@services/error-logger.service';
import {UrlManagerService} from '@services/url-manager.service';
import {NotificationsService} from '@providers/notifications/notifications.service';
import CentrifugeReceiveMessageResponse = NotificationsInterfaces.CentrifugeReceiveMessageResponse;
import CentrifugeConfig = NotificationsInterfaces.CentrifugeConfig;
import {NotyPosition} from '@components/noty/noty.enums';

/**
 * Сервис уведомлений
 * За основу взята "центрифуга"
 * https://fzambia.gitbooks.io/centrifugal/content/clients/javascript.html
 */

@Injectable()
export class InstantNotificationsService implements OnDestroy {

    /**
     * URL для получения конфигурации подключения к центрифуге
     * @type {string}
     */
    private configUrl = '/api/notification/get-config';

    /**
     * Объект центрифуги
     */
    private centrifuge;

    /**
     * Коллеция подписок центрифуги
     * */
    private subs: any[] = [];

    /**
     * Получены ли уведомления
     */
    private isFetched = false;

    constructor(
        private http: HttpClient,
        private events: EventsService,
        private errorsLogger: ErrorLoggerService,
        private urlManager: UrlManagerService,
        private notificationsService: NotificationsService,
        private notificationsProvider: NotificationsProvider,
    ) {
        /**
         * Временно отключаем все уведомления, ибо инфрастуктура еще не готова
         */
        this.subscribe();
    }

    async subscribe() {
        const conf = await this.getConfig();

        if (!conf || !conf.status) {
            return;
        }

        const confData = conf.data;

        this.centrifuge = new Centrifuge(confData);

        confData.channels.forEach(channel => {
            this.subs.push(
                this.centrifuge.subscribe(channel, (response: CentrifugeReceiveMessageResponse) => {
                    this.handleMessagesReceive(response.data.messages);
                })
            );
        });

        this.centrifuge.on('error', () => {
            console.info('Ошибка во время работы сервиса уведомлений...');
        });

        this.centrifuge.on('connect', () => {
            /**
             * Если сообщения уже были получены, то не будем их больше получать
             * Это на случай, если центрифуга прервет соединение и восстановит его, чтобы не добавлялись новые уведомления
             */
            if (this.isFetched) {
                return;
            }
            /**
             * Получаем все уже имеющиеся для профиля уведомления
             */
            this.notificationsService.get().pipe(
                take(1)
            ).subscribe((messages: NotificationsInterfaces.Message[]) => {
                this.isFetched = true;
                this.handleMessagesReceive(messages);
            }, () => {

            });
        });

        /**
         * Менее жесткие способы отловить ошибку подключения не срабатывают....
         */
        try {
            this.centrifuge.connect();
        } catch (e) {
            console.info('Не удается подключиться к сервису уведомлений...');
        }
    }

    ngOnDestroy() {
        if (this.centrifuge) {
            this.centrifuge.disconnect();
        }

        this.subs.forEach(s => s.unsubscribe());

        this.notificationsProvider.clearData();
    }

    /**
     * Обработчик получения сообщений.
     * Алерты или самое новое непрочитанное отображает сразу, остальные общие сообщения прокидывает
     * в провайдер уведомлений
     * @param messages
     */
    private handleMessagesReceive(messages: NotificationsInterfaces.Message[]) {
        try {
            const commonMessages = messages.filter(item => item.type === NotificationsEnums.Type.common);
            const alertMessages = messages.filter(item => item.type === NotificationsEnums.Type.alert);

            /**
             * Показываем либо alert уведомление, либо самое актуальное непрочитанное
             */
            const messageToShow =
                alertMessages && alertMessages.length ?
                    alertMessages[0] :
                    (commonMessages && commonMessages.length ? commonMessages.find(item => !item.is_read) : null);

            if (messageToShow) {
                this.events.notyShow$.next({
                    type: messageToShow.importance && messageToShow.importance > 1 ? 'warning' : 'info',
                    title: messageToShow.title,
                    content: messageToShow.extra && messageToShow.extra.data
                        ? [messageToShow.body, ...messageToShow.extra.data].join('<br>')
                        : messageToShow.body,
                    position: NotyPosition.bottomCenter,
                    hideOnRouteChange: false,
                    timeout: 0, // Перманентно, пока сам не закроет
                    controls: messageToShow.extra && messageToShow.extra.links && messageToShow.extra.links.length ?
                        messageToShow.extra.links.map(link => {
                            return {
                                title: link.label,
                                callback: () => {
                                    this.events.notyHide$.next();
                                    this.urlManager.navigateTo(link.url);
                                }
                            };
                        }) :
                        []
                });
            }

            this.notificationsProvider.addData(commonMessages);
        } catch (e) {
            console.error(e);
            this.errorsLogger.log(e);
        }
    }

    /**
     * Получить параметры конфигурации центрифуги
     * (куда подключаться, имя канала и т.п.).
     * @returns {Promise<any>}
     */
    private getConfig() {
        return this.http.get<{status: boolean, data: CentrifugeConfig}>(this.configUrl).pipe(
            retry(2),
            catchError(() => {
                return of(null);
            })
        ).toPromise();
    }
}
